<script lang="ts">
  import { onMount } from "svelte";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import Footer from "$components/forum/cards/Footer.svelte";
  import ForumCard from "$components/forum/cards/ForumCard.svelte";
  import Reactions from "$components/forum/Reactions/Reactions.svelte";
  import StackedAvatars from "$components/+blocks/StackedAvatars/StackedAvatars.svelte";
  import Upvote from "../Upvotes/Upvote.svelte";
  import { communitySlug } from "app/stores/community";
  import CreationTimeInterval from "$components/+blocks/Time/TimeAgo.svelte";
  import cx from "classnames";
  import api from "api";
  import { labels, labelsFetched } from "$stores/labels";
  import { tooltip } from "$actions/tooltip";
  import type { AvatarDetails } from "models/shared-types";
  import type { Thread as ThreadType } from "models/forums";
  import type { DropdownAction } from "$components/+blocks/Dropdown/fields/Action.svelte";
  import Reader from "$components/editor/Reader.svelte";
  import { fade } from "svelte/transition";
  import LoadingAtom from "$components/+blocks/Loading/LoadingAtom.svelte";
  import { CheckFilled, DashedCircle, Dot, LockSolid } from "$components/+blocks/OVIcon";

  export let thread: ThreadType;
  export let menuOptions: DropdownAction[] = [];
  export let canUpvote: boolean;
  export let isThreadUpvotesEnabled: boolean;
  export let showBreadCrumb: boolean = false;
  export let showChannelColour: boolean = false;
  export let avatars: AvatarDetails[];
  export let isQuote: boolean = false;

  const CONTENT_MAX_HEIGHT = 300;
  const MAX_LABELS_VISIBLE = 4;
  let fadeThread = true;
  let contentRef: HTMLElement;

  onMount(() => {
    if (thread.maximized) {
      return;
    }
    if (!contentRef) {
      // hitting this sometimes. Not sure why
      return;
    }

    // fade thread if content is too long
    const resizeObserver = new ResizeObserver((entries) => {
      const { height } = entries[0].contentRect;
      if (height >= CONTENT_MAX_HEIGHT) {
        fadeThread = true;
      } else {
        fadeThread = false;
      }
    });
    resizeObserver.observe(contentRef);
    return () => resizeObserver.disconnect();
  });

  let highValueActivityCount: number;
  $: {
    if (thread.isSubscribedToActivity !== undefined) {
      highValueActivityCount =
        (thread.isSubscribedToActivity ? thread.newRepliesCount! : 0) + thread.directRepliesCount!;
    }
  }
</script>

<ForumCard
  {isQuote}
  author={thread.author}
  time={thread.created}
  fields={menuOptions}
  pinned={thread.pinned}
  locked={thread.locked}
  hidden={thread.hidden}
  maximized={thread.maximized}
  {showBreadCrumb}
  crumb={thread.forumSpace.title}
  {showChannelColour}
  crumbColour={thread.forumSpace.color}
  type={"thread"}
>
  <div
    class={cx("relative overflow-hidden break-words", {
      "[mask-image:_linear-gradient(to_top,_transparent_4%,_black_33%)]":
        !thread.maximized && fadeThread,
    })}
    style:max-height={!thread.maximized && fadeThread ? `${CONTENT_MAX_HEIGHT}px` : undefined}
    bind:this={contentRef}
  >
    <div class="flex items-start justify-between gap-2.5">
      <div class="w-full">
        <div class="mt-1 mb-2.5 flex items-start gap-2">
          {#if thread.solvable}
            <div class="mt-1 shrink-0">
              {#if thread.solved}
                <OVIcon icon={CheckFilled} class="text-green-400" />
              {:else}
                <OVIcon icon={DashedCircle} class="text-neutral-400" />
              {/if}
            </div>
          {/if}
          <h3 class="text-md inline-block font-semibold text-neutral-600 dark:text-white">
            {thread.title}
          </h3>
        </div>

        <div class="text-neutral-400">
          <Reader content={thread.content} disableLinksInSSR />
        </div>
      </div>

      {#if isThreadUpvotesEnabled}
        <Upvote
          variant="vertical-regular"
          disabled={!canUpvote}
          count={thread.upvoteCount}
          isSelected={thread.userUpvoted}
          addUpvote={() =>
            api.communitiesThreadsUpvote({
              communitySlug: $communitySlug,
              uuid: thread.uuid,
            })}
          removeUpvote={() =>
            api.communitiesThreadsRemoveUpvote({
              communitySlug: $communitySlug,
              uuid: thread.uuid,
            })}
        />
      {/if}
    </div>
  </div>

  {#if $labelsFetched && thread.labels?.length}
    <div class="mt-4 flex flex-wrap gap-1.5" in:fade>
      {#each thread.labels.slice(0, MAX_LABELS_VISIBLE) as uuid (uuid)}
        {@const label = $labels[uuid]}
        <div
          class="flex w-fit items-center gap-1 overflow-hidden rounded bg-black/5 px-1 py-[2px] dark:bg-white/5"
        >
          <span style:color={label.color}>
            <OVIcon icon={Dot} size="size-3" />
          </span>
          <span class="truncate text-xs font-medium text-neutral-500 dark:text-neutral-400">
            {label?.name}
          </span>
          {#if label.teamOnly}
            <span
              use:tooltip
              title="Only team members can see this label"
              class="text-neutral-500 dark:text-neutral-400"
            >
              <OVIcon icon={LockSolid} size="size-3.5" />
            </span>
          {/if}
        </div>
      {/each}
      {#if thread.labels.length > MAX_LABELS_VISIBLE}
        <span
          use:tooltip
          title={thread.labels
            .slice(MAX_LABELS_VISIBLE)
            .map((uuid) => $labels[uuid].name)
            .join(", ")}
          class="rounded bg-black/5 px-1 py-[2px] text-xs font-medium text-neutral-500 dark:bg-white/5 dark:text-neutral-400"
        >
          +{thread.labels.length - MAX_LABELS_VISIBLE}
        </span>
      {/if}
    </div>
  {:else if thread.labels?.length}
    <!-- We know there are labels so reserve space to prevent content shift -->
    <div class="h-9"></div>
  {/if}

  <Footer>
    <svelte:fragment slot="left-section">
      <span
        class="text flex h-[20px] shrink-0 items-center space-x-1 rounded-[8px] py-2 text-sm md:text-[13px] dark:text-neutral-400 {isQuote
          ? 'text-neutral-400'
          : 'text-neutral-500'}"
      >
        {#if thread.repliers?.length}
          <span in:fade>
            <StackedAvatars {avatars} size="size-5" textSize="text-xxs" radius="rounded-[3px]" />
          </span>
        {:else}
          {#each Array(Math.min(thread.replyCount, 3)) as _}
            <LoadingAtom size="size-5" radius="rounded-[3px]" />
          {/each}
        {/if}
        <span class="shrink-0 px-1 text-xs font-medium text-blue-300">
          {thread.replyCount}
          {thread.replyCount === 1 ? "reply" : "replies"}
        </span>

        {#if !!highValueActivityCount}
          <span
            class={cx(
              "ml-2 flex h-[18px] min-w-[18px] items-center justify-center rounded",
              "bg-green-400/50 px-[5px] text-xxs font-semibold text-white"
            )}
          >
            {highValueActivityCount}
          </span>
        {/if}
      </span>
    </svelte:fragment>

    <svelte:fragment slot="middle-section">
      {#if !!thread.reactions?.length && !isQuote}
        <Reactions reactions={thread.reactions} />
      {/if}
    </svelte:fragment>

    <svelte:fragment slot="right-section">
      {#if !isQuote}
        <span
          class="mr-2.5 inline-flex gap-0.5 text-xs font-normal text-neutral-500 dark:text-neutral-400"
          >Last activity <CreationTimeInterval
            time={thread.lastRepliedAt}
            modified={new Date().toISOString()}
          /></span
        >
      {/if}
    </svelte:fragment>
  </Footer>
</ForumCard>
